import { Box, Grid, Stack } from "@mui/material";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { CreateInCollectionButton } from "../../../../../components/CreateInCollectionButton";
import EasyFormColumn from "../../../../../components/Forms/EasyFormColumn";
import EasyFormMany from "../../../../../components/Forms/EasyFormMany";
import { TextFieldRender } from "../../../../../components/Forms/Renders/TextFieldRender";
import Loading from "../../../../../components/loading";
import { PaginationQueryPackage } from "../../../../../models/API/QueryParams/PaginationQueryPackage";
import { OrganisationContact, OrganisationContactLabel } from "../../../../../models/OrganisationContact";
import { PermissionEnum } from "../../../../Permission/PermissionEnum";
import { AbilityContext } from "../../../../UserApi/logic/Can";
import { getOrganisationContactValidationScheme } from "../../../organisationContactSchemeValidation";
import { useAddOrganisationContactMutation, useDeleteOrganisationContactMutation, useGetOrganisationContactsByOrganisationIdQuery, useUpdateOrganisationContactMutation } from "../../../redux/organisationContactApiSlice";


export const getOrganisationContactFields = (withoutName: boolean, showLabel: boolean, parentFieldName?: string) => (contacts: OrganisationContact[] | undefined) => {
    return contacts?.map((contact) => [
        ...(withoutName ? [] : [
            new TextFieldRender({
                displayText: "Title and name",
                showLabel: showLabel,
                fieldName: parentFieldName ?? "",
                nestedfieldName: "name",
                type: "text",
                uniqueId: contact.uuid,
                width: "40%"
            })]),
        new TextFieldRender({
            displayText: "Email",
            showLabel: showLabel,
            fieldName: parentFieldName ?? "",
            nestedfieldName: "email",
            type: "text",
            uniqueId: contact.uuid,
            width: withoutName ? "37%" : "35%"
        }),
        new TextFieldRender({
            displayText: "Phone",
            showLabel: showLabel,
            fieldName: parentFieldName ?? "",
            nestedfieldName: "phone",
            type: "text",
            uniqueId: contact.uuid,
            width: withoutName ? "63%" : "25%"
        }),
    ]) ?? []
}

export default function OrganisationContactsTab(props: { organisationId: string }) {
    const { t } = useTranslation()
    const orgId = props.organisationId
    const contactsResult = useGetOrganisationContactsByOrganisationIdQuery({ uuid: props.organisationId, pagination: new PaginationQueryPackage() })

    const ability = useContext(AbilityContext)


    const ContactForm = (props: { label: OrganisationContactLabel, title: string, withoutName?: boolean }) => {
        const getFields = getOrganisationContactFields(props.withoutName ?? false, false)
        return (
            <EasyFormMany
                confirmDeletionText="Are you sure you wish to delete this contact?"
                deleteDisabled={contactsResult.data != undefined && contactsResult.data?.filter(contact => contact.label === props.label).length < 2}
                deleteDisabledTooltip="At least one contact is required"
                isAllowedToDelete={() => ability.can(PermissionEnum.ORGANISATION_WRITE, { uuid: orgId })}
                isAllowedToEdit={() => ability.can(PermissionEnum.ORGANISATION_WRITE, { uuid: orgId })}
                isAllowedToCreate={() => ability.can(PermissionEnum.ORGANISATION_WRITE, { uuid: orgId })}
                ressourceOwnerId={orgId}
                arrayPath=""
                initialValue={contactsResult.data?.filter(contact => contact.label === props.label) ?? []}
                deleteMutation={useDeleteOrganisationContactMutation}
                createButtonJsx={
                    <CreateInCollectionButton
                        validationScheme={getOrganisationContactValidationScheme()}
                        ressourceOwnerId={orgId}
                        fieldEditorRenders={getFields([new OrganisationContact(props.label)])[0]}
                        tooltip={t("Create ") + t(props.title)}
                        createNew={() => new OrganisationContact(props.label)}
                        saveMutation={useAddOrganisationContactMutation} />
                }
                editButtonJsx={(contact: OrganisationContact) =>
                    <CreateInCollectionButton
                        validationScheme={getOrganisationContactValidationScheme()}
                        toEdit={contact}
                        createNew={() => { throw new Error("Dont create new here") }}
                        ressourceOwnerId={orgId}
                        fieldEditorRenders={getFields([contact])[0]}
                        tooltip={t("Edit ") + t(props.title)}
                        saveMutation={useUpdateOrganisationContactMutation}
                    />
                }
                SignupSchema={getOrganisationContactValidationScheme()}
                idToFetch={orgId}
                columns={[
                    new EasyFormColumn(props.title, "", [], "overline")
                ]}
                getFieldRows={getFields}
            />
        )
    }

    return (
        contactsResult.data ?
            <Stack spacing={3}>
                <ContactForm label={OrganisationContactLabel.CEO} title="Overall contact / CEO" />
                <ContactForm label={OrganisationContactLabel.SECURITY_MANAGER} title="Security manager" />
                <ContactForm label={OrganisationContactLabel.ORGANISATIONAL_MANAGER} title="Organisational manager" />
                <ContactForm label={OrganisationContactLabel.FINANCIAL_MANAGER} title="Financial manager" />
                <ContactForm label={OrganisationContactLabel.SERVICE_DESK} title="Service desk and on-call" withoutName />
            </Stack> : <Loading />
    )
}